import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import React, { Suspense, useEffect } from "react";

import Login from "./pages/Login";
import NotFound from "./pages/NotFound";
import DashboardProjects from "./pages/DashboardProjects";
import { SecureRoute } from "./components/auth/SecureRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import Settings from "./pages/Settings";
import { RootRedirect } from "./components/auth/RootRedirect";

// Import i18n configuration
import "./i18n";
import { AuthProvider } from "./contexts/AuthContext";
import { MicrosoftAuthProvider } from "./contexts/MicrosoftAuthContext";
import DebugInfo from "./components/debug/DebugInfo";
import VersionUpdateDialog from "@/components/VersionUpdateDialog";
import LanguageSync from "./components/LanguageSync";
import { PostHogIdentifier } from "./components/PostHogIdentifier";
import { ErrorBoundary, ErrorTracker } from "./components/ErrorBoundary";
import { OfflineIndicator } from "./components/OfflineIndicator";
import { PWAInstallModal } from "./components/PWAInstallModal";
import { isSessionError } from "@/utils/authErrorHandler";
import { auth } from "@/services/supabase";

import ProcurementLanding from "./pages/procurement/ProcurementLanding";
import ProcurementUpload from "./pages/procurement/ProcurementUpload";
import ProcurementPackageDetail from "./pages/procurement/ProcurementPackageDetail";
import { OrgFeatureFlagsProvider } from "./contexts/OrgFeatureFlagsContext";
import { OrgLayout } from "./layouts/OrgLayout";
import { ProjectLayout } from "./layouts/ProjectLayout";
import TenderPage from "./pages/project/TenderPage";
import ProcurementPage from "./pages/project/ProcurementPage";
import AIDocumentsPage from "./pages/project/AIDocumentsPage";
import AIDocumentDetailPage from "./pages/project/AIDocumentDetailPage";
import { LegacyProjectRedirect } from "./components/redirects/LegacyProjectRedirect";
import { LegacyDashboardRedirect } from "./components/redirects/LegacyDashboardRedirect";
import TimelineTestPage from "./pages/test/TimelineTestPage";
import StyleKitchenSinkAccessGate from "./components/auth/StyleKitchenSinkAccessGate";
import TitleBlockCuratorPage from "./pages/eval/TitleBlockCuratorPage";
import KnowledgeBaseLandingPage from "./pages/knowledge-base/KnowledgeBaseLandingPage";
import KnowledgeBaseProjectPage from "./pages/knowledge-base/KnowledgeBaseProjectPage";
import GroupingEvalPage from "./pages/eval/GroupingEvalPage";
import { LegacyProcurementRedirect } from "./components/redirects/LegacyProcurementRedirect";
import { LegacyReportRedirect } from "./components/redirects/LegacyReportRedirect";
import { LegacyPackagesRedirect } from "./components/redirects/LegacyPackagesRedirect";
import TetrisPage from "./pages/TetrisPage";
import { MaintenanceScreen } from "./components/MaintenanceScreen";

/**
 * Flip to `true` to show a full-screen maintenance page.
 * Nothing else loads — no auth, no routes, no API calls.
 * Remember to set back to `false` and deploy when done.
 */
const MAINTENANCE_MODE = true;

const AgentationLazy = import.meta.env.DEV
  ? React.lazy(() => import("agentation").then((m) => ({ default: m.Agentation })))
  : null;

/**
 * Guard to prevent multiple concurrent sign-out attempts.
 * When multiple queries fail simultaneously with session errors,
 * we only want to handle the sign-out once.
 */
let isHandlingSessionError = false;

/**
 * Global error handler for React Query.
 * Catches auth errors (invalid JWT, expired session) and handles them
 * gracefully by signing out the user instead of tracking them as errors.
 *
 * Uses a closure to access queryClient after it's initialized.
 */
const createQueryErrorHandler = (getQueryClient: () => QueryClient) => {
  return (error: unknown) => {
    if (isSessionError(error)) {
      // Prevent multiple concurrent sign-out attempts
      if (isHandlingSessionError) return;
      isHandlingSessionError = true;

      // Session is invalid - sign out gracefully
      // This happens when user switches accounts or has a stale session
      console.info("Session expired or invalid, signing out...");

      // Clear cache first to prevent infinite error loops
      getQueryClient().clear();

      auth
        .signOut()
        .then(() => {
          // Navigate to login after successful sign-out
          window.location.href = "/login";
        })
        .catch(() => {
          // If signOut fails, force full page reload to clear all state
          window.location.reload();
        })
        .finally(() => {
          // Reset flag for edge cases where navigation doesn't occur.
          // In normal operation, page navigation resets all JS state anyway.
          isHandlingSessionError = false;
        });
      return;
    }
    // Other errors will be caught by ErrorTracker/ErrorBoundary
    console.error("Query error:", error);
  };
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: createQueryErrorHandler(() => queryClient),
  }),
  mutationCache: new MutationCache({
    onError: createQueryErrorHandler(() => queryClient),
  }),
  defaultOptions: {
    queries: {
      // Don't retry on auth errors
      retry: (failureCount, error) => {
        if (isSessionError(error)) return false;
        return failureCount < 3;
      },
    },
  },
});

const App = () => {
  useEffect(() => {
    if ("serviceWorker" in navigator && import.meta.env.PROD) {
      let intervalId: ReturnType<typeof setInterval> | undefined;

      navigator.serviceWorker
        .register("/sw.js")
        .then((registration) => {
          // Helper to safely update registration (can fail during deployments or on Safari)
          const safeUpdate = () => {
            registration.update().catch(() => {
              // Silently ignore update errors - these are expected during deployments
              // or on browsers with strict service worker lifecycle (Safari/iOS)
            });
          };

          // Check for updates more frequently (every 5 minutes)
          intervalId = setInterval(safeUpdate, 5 * 60 * 1000);

          // Also check for updates when the page becomes visible
          const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
              safeUpdate();
            }
          };
          document.addEventListener("visibilitychange", handleVisibilityChange);

          // Listen for service worker updates
          registration.addEventListener("updatefound", () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                  // New service worker is installed and ready
                  // The version check hook will detect the version mismatch
                  // and show the update dialog
                  console.log(
                    "New service worker installed, version check will trigger update dialog",
                  );
                }
              });
            }
          });

          // Store cleanup for visibility listener
          return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
          };
        })
        .catch((registrationError) => {
          console.log("SW registration failed: ", registrationError);
        });

      // Cleanup function to clear the interval when component unmounts
      return () => {
        if (intervalId !== undefined) {
          clearInterval(intervalId);
        }
      };
    }
  }, []);

  if (MAINTENANCE_MODE) {
    return <MaintenanceScreen />;
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <PostHogIdentifier>
              <ErrorTracker />
              <LanguageSync />
              <MicrosoftAuthProvider>
                <OrgFeatureFlagsProvider>
                  <TooltipProvider>
                    <OfflineIndicator />
                    <Toaster />
                    <Suspense fallback={<div>Loading...</div>}>
                      <Routes>
                        <Route path="/" element={<RootRedirect />} />
                        <Route path="/en" element={<RootRedirect />} />
                        <Route path="/login" element={<Login />} />
                        <Route
                          path="/style"
                          element={
                            <SecureRoute>
                              <StyleKitchenSinkAccessGate />
                            </SecureRoute>
                          }
                        />
                        <Route path="/tetris" element={<TetrisPage />} />

                        {/* Legacy dashboard redirects */}
                        <Route
                          path="/dashboard"
                          element={
                            <SecureRoute>
                              <LegacyDashboardRedirect />
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/dashboard/*"
                          element={
                            <SecureRoute>
                              <LegacyDashboardRedirect />
                            </SecureRoute>
                          }
                        />

                        {/* Legacy settings redirect */}
                        <Route
                          path="/settings"
                          element={
                            <SecureRoute>
                              <Settings />
                            </SecureRoute>
                          }
                        />

                        {/* Legacy admin redirect */}
                        <Route
                          path="/admin"
                          element={
                            <SecureRoute>
                              <LegacyDashboardRedirect />
                            </SecureRoute>
                          }
                        />

                        {/* Legacy project redirects */}
                        <Route
                          path="/project/setup/:projectId"
                          element={
                            <SecureRoute>
                              <LegacyProjectRedirect />
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/project/overview/:projectId"
                          element={
                            <SecureRoute>
                              <LegacyProjectRedirect />
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/procurement"
                          element={
                            <SecureRoute>
                              <ProcurementLanding />
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/procurement/upload"
                          element={
                            <SecureRoute>
                              <ProcurementUpload />
                            </SecureRoute>
                          }
                        />
                        {/* Legacy procurement redirects */}
                        <Route
                          path="/procurement/projects/:projectId"
                          element={
                            <SecureRoute>
                              <LegacyProcurementRedirect />
                            </SecureRoute>
                          }
                        />
                        <Route
                          path="/procurement/projects/:projectId/packages/:packageId"
                          element={
                            <SecureRoute>
                              <LegacyProcurementRedirect />
                            </SecureRoute>
                          }
                        />

                        {/* New org-scoped routes */}
                        <Route
                          path="/:orgId"
                          element={
                            <SecureRoute>
                              <OrgLayout />
                            </SecureRoute>
                          }
                        >
                          {/* Org index redirects to projects */}
                          <Route index element={<Navigate to="projects" replace />} />

                          {/* Projects list (reuse DashboardProjects for now) */}
                          <Route path="projects" element={<DashboardProjects />} />

                          {/* Settings */}
                          <Route path="settings" element={<Settings />} />

                          {/* Dev test routes */}
                          <Route path="_dev/timeline" element={<TimelineTestPage />} />
                          <Route
                            path="_dev/title-block-curator"
                            element={<TitleBlockCuratorPage />}
                          />
                          {/* Knowledge Base routes */}
                          <Route path="knowledge-base" element={<KnowledgeBaseLandingPage />} />
                          <Route
                            path="knowledge-base/:projectId"
                            element={<KnowledgeBaseProjectPage />}
                          />
                          <Route
                            path="_dev/grouping"
                            element={
                              <AdminRoute>
                                <GroupingEvalPage />
                              </AdminRoute>
                            }
                          />

                          {/* Project routes */}
                          <Route path=":projectId" element={<ProjectLayout />}>
                            {/* Project index redirects to tender */}
                            <Route index element={<Navigate to="tender" replace />} />

                            {/* Section routes - placeholders for now */}
                            <Route path="tender" element={<TenderPage />} />
                            <Route path="documents" element={<AIDocumentsPage />} />
                            <Route path="documents/:docId" element={<AIDocumentDetailPage />} />
                            {/* Legacy redirects for backward compatibility */}
                            <Route
                              path="reports"
                              element={<Navigate to="../documents" replace />}
                            />
                            <Route path="reports/:reportId" element={<LegacyReportRedirect />} />
                            <Route path="procurement" element={<ProcurementPage />} />
                            <Route
                              path="procurement/:packageId"
                              element={<ProcurementPackageDetail />}
                            />
                            {/* Legacy redirect: /procurement/packages/:id → /procurement/:id */}
                            <Route
                              path="procurement/packages/:packageId"
                              element={<LegacyPackagesRedirect />}
                            />
                          </Route>
                        </Route>

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                    <DebugInfo />
                    <VersionUpdateDialog />
                    <PWAInstallModal />
                    {AgentationLazy && (
                      <Suspense fallback={null}>
                        <AgentationLazy />
                      </Suspense>
                    )}
                  </TooltipProvider>
                </OrgFeatureFlagsProvider>
              </MicrosoftAuthProvider>
            </PostHogIdentifier>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
